import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

interface NotePopoverProps {
  note: string | null;
  onSave: (text: string) => void;
  onDelete: () => void;
}

function NoteAddIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v3M16 2v3M3.5 9.09h17M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z" />
      <path d="M15.695 13.7h.009M15.695 16.7h.009M11.995 13.7h.01M11.995 16.7h.01M8.294 13.7h.01M8.294 16.7h.01" />
    </svg>
  );
}

function NoteFilledIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v3M16 2v3M7 11h8M7 15h5M15 22H9c-5 0-6-2.06-6-6.18V9.65c0-4.7 1.67-6.26 5-6.52h8c3.33.26 5 1.82 5 6.52V16" />
      <path d="M21 16l-6 6v-3c0-2 1-3 3-3h3Z" />
    </svg>
  );
}

function usePopoverPosition(triggerRef: React.RefObject<HTMLDivElement | null>, isOpen: boolean) {
  const [style, setStyle] = useState<React.CSSProperties>({});

  const update = useCallback(() => {
    if (!triggerRef.current || !isOpen) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const popoverHeight = 160;

    if (spaceBelow < popoverHeight) {
      setStyle({ position: "fixed", bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right, zIndex: 50 });
    } else {
      setStyle({ position: "fixed", top: rect.bottom + 4, right: window.innerWidth - rect.right, zIndex: 50 });
    }
  }, [triggerRef, isOpen]);

  useEffect(() => {
    update();
    if (!isOpen) return;
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen, update]);

  return style;
}

export function NotePopover({ note, onSave, onDelete }: Readonly<NotePopoverProps>) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(note ?? "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverStyle = usePopoverPosition(triggerRef, open || showDeleteConfirm);

  useEffect(() => {
    if (open) setText(note ?? "");
  }, [open, note]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
      setShowDeleteConfirm(false);
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        setShowDeleteConfirm(false);
      }
    }
    if (open || showDeleteConfirm) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, showDeleteConfirm]);

  let popoverContent = null;

  if (showDeleteConfirm) {
    popoverContent = (
      <div ref={popoverRef} style={popoverStyle} className="w-[232px] rounded-xl border border-stroke-primary bg-bg-primary p-4 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]">
        <p className="text-sm font-medium text-text-primary mb-1">Delete note</p>
        <p className="text-sm text-text-secondary mb-3">Are you sure you want to delete this note?</p>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="flex-1 h-8 rounded-lg border border-stroke-primary text-sm text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onDelete();
              setShowDeleteConfirm(false);
              setOpen(false);
            }}
            className="flex-1 h-8 rounded-lg bg-system-red text-text-btn text-sm"
          >
            Delete note
          </button>
        </div>
      </div>
    );
  } else if (open) {
    popoverContent = (
      <div ref={popoverRef} style={popoverStyle} className="w-[232px] rounded-xl border border-stroke-primary bg-bg-primary p-2 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]">
        <div className="flex items-start justify-between">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Note text"
            rows={2}
            className="flex-1 text-sm leading-[1.5] text-text-primary placeholder:text-text-tertiary outline-none resize-none bg-transparent"
          />
          <button
            onClick={() => setOpen(false)}
            className="text-text-tertiary hover:text-text-secondary shrink-0 ml-1"
            aria-label="Close note"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => {
              onSave(text);
              setOpen(false);
            }}
            disabled={!text.trim()}
            className="h-[26px] px-2 rounded-lg bg-btn-action text-text-btn text-sm disabled:opacity-40"
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={triggerRef}>
      <div className="flex items-center gap-1">
        <NoteIconButton hasNote={!!note} onClick={() => setOpen(!open)} />
        {note && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-text-tertiary hover:text-system-red"
            aria-label="Delete note"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
      {popoverContent && createPortal(popoverContent, document.body)}
    </div>
  );
}

function NoteIconButton({ hasNote, onClick }: Readonly<{ hasNote: boolean; onClick: () => void }>) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center w-[32px] h-[32px] rounded-xl shrink-0 ${
        hasNote
          ? "bg-brand-brown-6 text-brand-brown"
          : "bg-bg-primary border border-stroke-primary text-text-tertiary"
      }`}
      aria-label={hasNote ? "Edit note" : "Add note"}
    >
      {hasNote ? <NoteFilledIcon /> : <NoteAddIcon />}
    </button>
  );
}
